/**
 * Atmosphere boundary — dashed circle marker (F-102: 이전 명칭 "ozone").
 *
 * Replaces the previous shader-sphere with a simple dashed Line in the
 * XY plane around Earth. Reads at a glance as "boundary line" — the
 * line you must not let meteors settle past. Color brightens toward
 * red as more meteors approach a breach (driven by setDanger). On a
 * confirmed breach, a brief pulse boosts brightness + width.
 *
 * The playfield is camera-facing (z=0 clamp), so a flat XY circle is
 * the natural depiction. Dashed segments remove any "wall feel" that
 * a solid circle would imply — the ring is informational, not solid.
 */

import {
  BufferAttribute,
  BufferGeometry,
  Color,
  LineBasicMaterial,
  LineSegments,
} from "three";

const SEGMENTS = 240;
const DASH_PATTERN = 4; // every Nth segment is filled — denser tighter dashes

const IDLE_COLOR = 0x6cd4ff;
const WARN_COLOR = 0xff5b5b;

export interface AtmosphereRingOptions {
  earthRadius: number;
  atmosphereRadius: number;
}

export interface AtmosphereRing {
  /** Three.js object to add to the scene. */
  mesh: LineSegments;
  /** 0 = no danger, 1 = breach imminent. Drives color blend. */
  setDanger(value: number): void;
  /** Brief flash on actual breach. */
  flashBreach(): void;
  tick(dt: number): void;
  detach(): void;
}

function buildDashedRing(radius: number): BufferGeometry {
  // Build vertex pairs: each "dash" is two consecutive vertices that
  // form a line segment; gaps are skipped indices. THREE.Line draws
  // pairs as discrete segments only when we use Line + LineSegments
  // primitive — but Line connects all consecutive points. Instead we
  // emulate dashes by emitting only the lit segments and using
  // Line with `index` + `LinePieces`. Simplest correct approach:
  // build a LineSegments-style geometry — pairs of points.
  const positions: number[] = [];
  const total = SEGMENTS;
  for (let i = 0; i < total; i += 1) {
    if (i % DASH_PATTERN >= DASH_PATTERN / 2) continue; // skip half
    const a0 = (i / total) * Math.PI * 2;
    const a1 = ((i + 1) / total) * Math.PI * 2;
    positions.push(
      Math.cos(a0) * radius, Math.sin(a0) * radius, 0,
      Math.cos(a1) * radius, Math.sin(a1) * radius, 0,
    );
  }
  const geo = new BufferGeometry();
  geo.setAttribute(
    "position",
    new BufferAttribute(new Float32Array(positions), 3),
  );
  return geo;
}

export function createAtmosphereRing(opts: AtmosphereRingOptions): AtmosphereRing {
  // LineSegments-style: each pair of vertices is its own segment, so
  // we use Line with the geometry above and treat it as "line pieces".
  // Three.js's LineSegments class does exactly this.
  const geometry = buildDashedRing(opts.atmosphereRadius);
  const idleColor = new Color(IDLE_COLOR);
  const warnColor = new Color(WARN_COLOR);
  const material = new LineBasicMaterial({
    color: idleColor.clone(),
    transparent: true,
    opacity: 0.55,
    depthWrite: false,
    linewidth: 2,
  });
  // LineSegments interprets each consecutive pair of vertices as its
  // own segment — perfect for a dashed-circle layout where we already
  // emit "lit" pairs and skip the gaps in the geometry above.
  const ring = new LineSegments(geometry, material);
  ring.name = "atmosphere-ring";
  ring.renderOrder = -2;

  let danger = 0;
  let dangerTarget = 0;
  let flashT = 0;
  let elapsed = 0;

  const setDanger = (value: number): void => {
    dangerTarget = Math.min(1, Math.max(0, value));
  };

  const flashBreach = (): void => {
    flashT = 1;
  };

  const tick = (dt: number): void => {
    elapsed += dt;
    danger += (dangerTarget - danger) * Math.min(1, dt * 4);
    if (flashT > 0) flashT = Math.max(0, flashT - dt * 3);
    const warnAmount = Math.max(danger, flashT);
    // Pulse the warning color so it reads as alarm.
    const pulse = 0.5 + 0.5 * Math.sin(elapsed * 7);
    const blend = warnAmount * (0.5 + 0.5 * pulse);
    material.color.copy(idleColor).lerp(warnColor, blend);
    material.opacity = 0.55 + warnAmount * 0.4 + flashT * 0.3;
    void opts.earthRadius;
  };

  const detach = (): void => {
    geometry.dispose();
    material.dispose();
    ring.removeFromParent();
  };

  return { mesh: ring, setDanger, flashBreach, tick, detach };
}

export const __testing = { IDLE_COLOR, WARN_COLOR, SEGMENTS };
